import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { type EnvName } from "./api";
import { useApi } from "./useApi";

/** Global interrupt only. Full conflict prose lives once on the dashboard / brief. */
export function BoardAlertBar({ env }: { env: EnvName }) {
  const api = useApi(env);
  const location = useLocation();
  const { data } = useQuery({
    queryKey: ["dashboard", env],
    queryFn: api.dashboard,
    staleTime: 0,
  });

  if (!data) return null;

  const blocking = data.blocking_conflicts ?? [];
  const scanError = data.conflict_scan_error;
  const briefBlocked = data.brief_state === "blocked";

  if (!scanError && blocking.length === 0 && !briefBlocked) {
    return null;
  }

  const prefix = `/${env}`;
  const path = location.pathname;
  const onDashboard = path === prefix || path === `${prefix}/`;
  const onBrief = path === `${prefix}/brief` || path.startsWith(`${prefix}/brief/`);

  const reviewTo = onBrief
    ? { pathname: `${prefix}/brief`, hash: "#brief-conflicts" }
    : onDashboard
      ? { pathname: prefix, hash: "#blocking-conflicts" }
      : { pathname: prefix, hash: "#blocking-conflicts" };

  const linkLabel = onDashboard
    ? "Review below"
    : onBrief
      ? "See below"
      : "Review conflicts";

  return (
    <div className="board-alert-bar" role="status" aria-live="polite">
      {scanError ? (
        <p className="board-alert board-alert--error">
          ConflictFinder error: {scanError}
        </p>
      ) : null}

      {blocking.length > 0 ? (
        <div className="board-alert board-alert--conflict board-alert--compact">
          <strong>
            {blocking.length} blocking conflict{blocking.length === 1 ? "" : "s"}
          </strong>
          <span className="board-alert-lead">{blocking[0]?.message}</span>
          <Link to={reviewTo} className="board-alert-link">
            {linkLabel}
          </Link>
        </div>
      ) : null}

      {briefBlocked && blocking.length === 0 ? (
        <p className="board-alert board-alert--brief">
          Brief is blocked.{" "}
          <Link to={`${prefix}/brief`} className="board-alert-link">
            Review brief
          </Link>
        </p>
      ) : null}
    </div>
  );
}
